/**
 * @fileoverview Componente responsável por exibir detalhes completos de um lead.
 */

import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Menu from "@material-ui/core/Menu";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import EmailIcon from "@material-ui/icons/Email";
import FingerprintIcon from "@material-ui/icons/Fingerprint";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import TextField from "@material-ui/core/TextField";
import Collapse from "@material-ui/core/Collapse";
import Avatar from "@material-ui/core/Avatar";
import { Trash2, Sparkles, ChevronDown, Eye, Pencil, Plus, X, File, MoreVertical, Users, Image as ImageIcon, FileText, UploadCloud, Calendar } from "lucide-react";
import InputMask from "react-input-mask";
import NumberFormat from "react-number-format";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission from "../../hooks/usePermission";
import AiReformulateDescriptionModal from "../AiReformulateDescriptionModal";
import LeadTriageSection from "../Triage/LeadTriageSection";
import { markdownToHtml, looksLikeMarkdown } from "../../utils/markdownToHtml";
import {
  AssigneePicker,
  PrioritySegmented,
  SlaPopoverButton,
  CardTitleHero,
  CardDescriptionEditor,
  CardCustomFieldsSection,
  FIELD_TYPES as SHARED_FIELD_TYPES,
  PRESETS as SHARED_PRESETS,
  generateFieldId as sharedGenerateFieldId,
  quillIsEmpty,
  useLeadSharedStyles,
} from "../LeadShared";

// Sanitize HTML — allow safe tags only, strip scripts and event handlers
const ALLOWED_TAGS = new Set([
  'p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'del',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'pre', 'code', 'blockquote',
  'a', 'span', 'div', 'sub', 'sup', 'hr',
]);

const sanitizeHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const clean = (node) => {
    const children = Array.from(node.childNodes);
    children.forEach(child => {
      if (child.nodeType === 1) { // Element
        const tag = child.tagName.toLowerCase();
        if (!ALLOWED_TAGS.has(tag)) {
          // Replace with its children
          while (child.firstChild) node.insertBefore(child.firstChild, child);
          node.removeChild(child);
        } else {
          // Remove event handlers and dangerous attributes
          Array.from(child.attributes).forEach(attr => {
            const attrName = attr.name.toLowerCase();
            // handlers de evento (on*) e style com expression/javascript
            if (attrName.startsWith('on') || (attrName === 'style' && /expression|javascript:/i.test(attr.value || ''))) {
              child.removeAttribute(attr.name);
              return;
            }
            // protocolos perigosos em href/src (ex.: javascript:, data:, vbscript:)
            // — antes o sanitizer permitia <a href="javascript:..."> (XSS por clique)
            if (attrName === 'href' || attrName === 'src' || attrName === 'xlink:href') {
              const v = String(attr.value || '').replace(/\s+/g, '').toLowerCase();
              if (v && !/^(https?:|mailto:|tel:|#|\/|\.)/.test(v)) {
                child.removeAttribute(attr.name);
              }
            }
          });
          clean(child);
        }
      }
    });
  };
  clean(doc.body);
  return doc.body.innerHTML;
};

const PRIORITIES = {
  urgent: { label: "Maior", color: "#dc2626" },
  high: { label: "Alta", color: "#f59e0b" },
  normal: { label: "Média", color: "#3b82f6" },
  low: { label: "Baixa", color: "#22c55e" },
};

const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Maior", color: "#dc2626" },
  { value: "high", label: "Alta", color: "#f59e0b" },
  { value: "normal", label: "Média", color: "#3b82f6" },
  { value: "low", label: "Baixa", color: "#22c55e" },
];

const useStyles = makeStyles((theme) => ({
  modalDialog: {
    // Shell alinhado com CreateLeadModal — bg sólido escuro, borda e shadow
    // refinados pra visual premium consistente em criar/editar.
    '& .MuiDialog-paper': {
      borderRadius: 22,
      background: theme.palette.type === 'dark' ? '#0E0F12' : '#FFFFFF',
      border: theme.palette.type === 'dark'
        ? '1px solid rgba(255,255,255,0.06)'
        : '1px solid rgba(15,23,42,0.06)',
      boxShadow: theme.palette.type === 'dark'
        ? '0 32px 90px -16px rgba(0,0,0,0.7), 0 12px 32px -8px rgba(0,0,0,0.5)'
        : '0 32px 90px -16px rgba(15,23,42,0.20), 0 12px 32px -8px rgba(15,23,42,0.12)',
      maxWidth: 780,
      width: '100%',
      margin: 16,
      maxHeight: '92vh',
    },
  },
  modalTitle: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(1),
  },
  titleInput: {
    '& .MuiInputBase-input': {
      fontSize: '1.4rem',
      fontWeight: 700,
      color: theme.palette.type === 'dark' ? '#FFFFFF' : '#1e293b',
      padding: '6px 10px',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
  },
  titleActions: {
    display: 'flex',
    gap: 4,
    marginTop: 6,
  },
  descriptionHeader: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    gap: 4,
    '&:hover': {
      opacity: 0.8,
    },
  },
  descriptionToggle: {
    fontSize: 20,
    color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    transition: 'transform 0.2s',
  },
  descriptionText: {
    whiteSpace: 'pre-wrap',
    cursor: 'pointer',
    padding: '8px 10px',
    margin: '0 -10px',
    borderRadius: '8px',
    border: '2px solid transparent',
    transition: 'all 0.15s ease',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#334155',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: '8px 0',
      fontSize: '0.8rem',
    },
    '& th, & td': {
      border: theme.palette.type === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.15)'
        : '1px solid #e2e8f0',
      padding: '6px 10px',
      textAlign: 'left',
    },
    '& th': {
      fontWeight: 600,
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)',
    },
    '& ul, & ol': {
      paddingLeft: 20,
      margin: '4px 0',
    },
    '& p': {
      margin: '4px 0',
    },
    '& strong, & b': {
      fontWeight: 600,
    },
    '& code': {
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.06)',
      padding: '1px 4px',
      borderRadius: 4,
      fontSize: '0.8rem',
    },
    '& pre': {
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)',
      padding: '8px 12px',
      borderRadius: 6,
      overflow: 'auto',
      fontSize: '0.8rem',
    },
    '& h1, & h2, & h3, & h4': {
      marginTop: 8,
      marginBottom: 4,
    },
    '&:hover': {
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)',
      border: theme.palette.type === 'dark'
        ? '2px solid rgba(255, 255, 255, 0.1)'
        : '2px solid rgba(0, 0, 0, 0.08)',
    },
  },
  descriptionEmpty: {
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#94a3b8',
    fontStyle: 'italic',
    cursor: 'pointer',
    padding: '8px 10px',
    margin: '0 -10px',
    borderRadius: '8px',
    border: '2px solid transparent',
    transition: 'all 0.15s ease',
    '&:hover': {
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)',
      border: theme.palette.type === 'dark'
        ? '2px solid rgba(255, 255, 255, 0.1)'
        : '2px solid rgba(0, 0, 0, 0.08)',
    },
  },
  descriptionInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
  },
  descriptionEditor: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: theme.palette.type === 'dark'
      ? '2px solid rgba(91, 124, 255, 0.4)'
      : '2px solid rgba(61, 92, 253, 0.3)',
    background: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(0, 0, 0, 0.02)',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#334155',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    minHeight: 80,
    maxHeight: 300,
    overflowY: 'auto',
    outline: 'none',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: '8px 0',
      fontSize: '0.8rem',
    },
    '& th, & td': {
      border: theme.palette.type === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.15)'
        : '1px solid #e2e8f0',
      padding: '6px 10px',
      textAlign: 'left',
    },
    '& th': {
      fontWeight: 600,
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)',
    },
    '& ul, & ol': {
      paddingLeft: 20,
    },
    '& code': {
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.06)',
      padding: '1px 4px',
      borderRadius: 4,
      fontSize: '0.8rem',
    },
    '& pre': {
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)',
      padding: '8px 12px',
      borderRadius: 6,
      overflow: 'auto',
      fontSize: '0.8rem',
    },
  },
  modalContent: {
    padding: theme.spacing(3),
  },
  infoSection: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    // Visual igual ao CreateLeadModal: uppercase tiny, peso 700, sem azul
    // pesado (estilo Linear/Stripe).
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.42)' : '#71717A',
    marginBottom: 10,
    display: 'block',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2),
  },
  infoItem: {
    padding: theme.spacing(1.5),
    borderRadius: '12px',
    background: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.02)',
  },
  infoLabel: {
    fontSize: '0.75rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b',
    marginBottom: theme.spacing(0.5),
  },
  infoValue: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: theme.palette.type === 'dark' ? '#FFFFFF' : '#1e293b',
  },
  statusChip: {
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '4px 12px',
    borderRadius: '8px',
    color: '#FFFFFF',
  },
  statusSelect: {
    minWidth: 200,
    marginTop: theme.spacing(2),
  },
  attachmentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: theme.spacing(2),
  },
  attachmentCard: {
    borderRadius: '12px',
    overflow: 'hidden',
    border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: theme.palette.type === 'dark'
        ? '0 8px 24px rgba(91, 124, 255, 0.2)'
        : '0 4px 16px rgba(0, 0, 0, 0.1)',
    },
  },
  attachmentImage: {
    width: '100%',
    height: 120,
    objectFit: 'cover',
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
  },
  attachmentLabel: {
    padding: theme.spacing(1),
    fontSize: '0.75rem',
    textAlign: 'center',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
    background: theme.palette.type === 'dark'
      ? 'rgba(0, 0, 0, 0.3)'
      : 'rgba(0, 0, 0, 0.02)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  attachmentFilePlaceholder: {
    width: '100%',
    height: 120,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
    gap: theme.spacing(1),
  },
  pdfIcon: {
    fontSize: 48,
    color: '#ef4444',
  },
  docIcon: {
    fontSize: 48,
    color: '#3b82f6',
  },
  fileIcon: {
    fontSize: 48,
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#94a3b8',
  },
  deleteBtn: {
    color: theme.palette.type === 'dark' ? '#fca5a5' : '#ef4444',
    borderColor: theme.palette.type === 'dark' ? 'rgba(252, 165, 165, 0.3)' : 'rgba(239, 68, 68, 0.3)',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)',
      borderColor: theme.palette.type === 'dark' ? 'rgba(252, 165, 165, 0.5)' : 'rgba(239, 68, 68, 0.5)',
    },
  },
  coverImage: {
    width: '100%',
    maxHeight: 220,
    objectFit: 'cover',
    borderRadius: '12px',
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    '&:hover': {
      opacity: 0.85,
    },
  },
  coverActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  coverBtn: {
    fontSize: '0.75rem',
    textTransform: 'none',
    borderRadius: '8px',
  },
  pasteHint: {
    fontSize: '0.7rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#94a3b8',
    textAlign: 'center',
    padding: theme.spacing(1, 0),
    marginTop: theme.spacing(1),
  },
  attachmentMenuBtn: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    color: '#fff',
    padding: 3,
    opacity: 0,
    transition: 'opacity 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
    },
  },
  attachmentCardWrapper: {
    position: 'relative',
    '&:hover $attachmentMenuBtn': {
      opacity: 1,
    },
  },
  attachmentMenuItem: {
    padding: '6px 12px',
    minHeight: 'unset',
  },
  // Dynamic fields editing styles
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  fieldInput: {
    flex: 1,
  },
  fieldTextField: {
    marginBottom: 0,
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.02)',
    },
  },
  fieldMenu: {
    padding: 4,
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
    marginTop: 8,
    '&:hover': {
      color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    },
  },
  fieldTypeChip: {
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    padding: '2px 6px',
    borderRadius: 6,
    marginTop: 8,
    display: 'inline-block',
    whiteSpace: 'nowrap',
  },
  chipText: {
    background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b',
  },
  chipWhatsapp: {
    background: 'rgba(37, 211, 102, 0.15)',
    color: '#25d366',
  },
  chipCpf: {
    background: 'rgba(91, 124, 255, 0.15)',
    color: '#5B7CFF',
  },
  chipEmail: {
    background: 'rgba(245, 158, 11, 0.15)',
    color: '#f59e0b',
  },
  chipCurrency: {
    background: 'rgba(34, 197, 94, 0.15)',
    color: '#22c55e',
  },
  showOnCardDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: '#22c55e',
    flexShrink: 0,
    marginTop: 14,
  },
  addFieldBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: '12px',
    border: theme.palette.type === 'dark'
      ? '1px dashed rgba(147, 197, 253, 0.3)'
      : '1px dashed rgba(59, 130, 246, 0.3)',
    color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: 'transparent',
    fontSize: '0.85rem',
    fontWeight: 600,
    width: '100%',
    justifyContent: 'center',
    '&:hover': {
      background: theme.palette.type === 'dark'
        ? 'rgba(147, 197, 253, 0.08)'
        : 'rgba(59, 130, 246, 0.05)',
      borderColor: theme.palette.type === 'dark'
        ? 'rgba(147, 197, 253, 0.5)'
        : 'rgba(59, 130, 246, 0.4)',
    },
  },
  menuPresetDesc: {
    fontSize: '0.7rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
    marginTop: 2,
  },
  detailsEditGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  detailSelect: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.02)',
    },
  },
  labelEditor: {
    '& .MuiInputBase-input': {
      fontSize: '0.8rem',
      fontWeight: 600,
      padding: '4px 8px',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
    width: '100%',
    marginBottom: 4,
  },
  uploadZone: {
    border: theme.palette.type === 'dark'
      ? '2px dashed rgba(255, 255, 255, 0.15)'
      : '2px dashed rgba(0, 0, 0, 0.12)',
    borderRadius: '12px',
    padding: theme.spacing(2),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.01)',
    marginBottom: theme.spacing(2),
    '&:hover': {
      borderColor: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(0, 0, 0, 0.2)',
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)',
    },
  },
  uploadZoneDragging: {
    borderColor: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    background: theme.palette.type === 'dark'
      ? 'rgba(147, 197, 253, 0.1)'
      : 'rgba(59, 130, 246, 0.06)',
  },
  uploadIcon: {
    fontSize: 32,
    color: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.3)'
      : '#94a3b8',
    marginBottom: 4,
  },
  uploadText: {
    fontSize: '0.8rem',
    color: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.4)'
      : '#94a3b8',
  },
  uploadLimit: {
    fontSize: '0.7rem',
    color: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.25)'
      : '#cbd5e1',
    marginTop: 2,
  },
  pendingFileList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  pendingFileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px 4px 10px',
    borderRadius: '10px',
    background: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.02)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.06)',
    maxWidth: '100%',
  },
  pendingFileItemCover: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px 4px 10px',
    borderRadius: '10px',
    background: theme.palette.type === 'dark'
      ? 'rgba(139, 92, 246, 0.1)'
      : 'rgba(139, 92, 246, 0.06)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(139, 92, 246, 0.3)'
      : '1px solid rgba(139, 92, 246, 0.2)',
    maxWidth: '100%',
  },
  pendingFileThumb: {
    width: 28,
    height: 28,
    borderRadius: 6,
    objectFit: 'cover',
    flexShrink: 0,
  },
  pendingFileIconSmall: {
    fontSize: 20,
    flexShrink: 0,
  },
  pendingFileName: {
    fontSize: '0.75rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#475569',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 160,
  },
  pendingFileSize: {
    fontSize: '0.65rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#94a3b8',
    flexShrink: 0,
  },
  pendingFileRemove: {
    padding: 2,
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#94a3b8',
    '&:hover': { color: '#ef4444' },
  },
  pendingFileCoverBtn: {
    padding: 2,
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#94a3b8',
    '&:hover': { color: '#8b5cf6' },
  },
  pendingFileCoverActive: {
    padding: 2,
    color: '#8b5cf6',
  },
  uploadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  // Checklist styles
  checklistHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  checklistProgress: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  checklistBar: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
  },
  checklistBarFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.3s ease, background-color 0.3s ease',
  },
  checklistPercent: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b',
    minWidth: 30,
    textAlign: 'right',
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
    marginBottom: 2,
    borderRadius: 8,
    padding: '2px 4px 2px 0',
    transition: 'background 0.15s',
    '&:hover': {
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.04)'
        : 'rgba(0, 0, 0, 0.02)',
    },
    '&:hover $checklistDeleteBtn': {
      opacity: 1,
    },
  },
  checklistCheckbox: {
    padding: 6,
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#94a3b8',
    '&.Mui-checked': {
      color: '#22c55e',
    },
  },
  checklistTextWrapper: {
    flex: 1,
    minWidth: 0,
    paddingTop: 5,
  },
  checklistText: {
    fontSize: '0.85rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#334155',
    lineHeight: 1.4,
    cursor: 'text',
  },
  checklistTextDone: {
    fontSize: '0.85rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.35)' : '#94a3b8',
    textDecoration: 'line-through',
    lineHeight: 1.4,
    cursor: 'text',
  },
  checklistInput: {
    '& .MuiInputBase-input': {
      fontSize: '0.85rem',
      padding: '4px 8px',
      color: theme.palette.type === 'dark' ? '#FFFFFF' : '#1e293b',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    },
  },
  checklistDeleteBtn: {
    padding: 4,
    opacity: 0,
    transition: 'opacity 0.15s',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.25)' : '#94a3b8',
    marginTop: 4,
    '&:hover': {
      color: '#ef4444',
    },
  },
  checklistAddBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: '6px 12px',
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.45)' : '#64748b',
    cursor: 'pointer',
    fontSize: '0.8rem',
    transition: 'all 0.15s',
    '&:hover': {
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.04)',
      color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    },
  },
  // Comments styles
  commentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  commentItem: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
  },
  commentAvatar: {
    width: 32,
    height: 32,
    fontSize: '0.75rem',
    fontWeight: 600,
    flexShrink: 0,
    marginTop: 2,
  },
  commentBody: {
    flex: 1,
    minWidth: 0,
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginBottom: 2,
  },
  commentAuthor: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#FFFFFF' : '#1e293b',
  },
  commentTime: {
    fontSize: '0.7rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.35)' : '#94a3b8',
  },
  commentText: {
    fontSize: '0.85rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#334155',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  commentDeleteBtn: {
    padding: 3,
    opacity: 0,
    transition: 'opacity 0.15s',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1',
    marginLeft: 'auto',
    flexShrink: 0,
    marginTop: 2,
    '&:hover': {
      color: '#ef4444',
      opacity: 1,
    },
  },
  commentItemHover: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    '&:hover $commentDeleteBtn': {
      opacity: 1,
    },
  },
  commentInputRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
  commentInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.02)',
    },
    '& .MuiInputBase-input': {
      fontSize: '0.85rem',
    },
  },
  commentSendBtn: {
    minWidth: 'unset',
    borderRadius: 10,
    padding: '8px 16px',
    textTransform: 'none',
    alignSelf: 'flex-end',
  },
}));

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const stringToColor = (str) => {
  if (!str) return '#9e9e9e';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#6366f1', '#f43f5e'];
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatCommentTime = (date) => {
  if (!date) return '';
  const now = new Date();
  const d = new Date(date);
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}min`;
  if (diffH < 24) return `${diffH}h`;
  if (diffD < 7) return `${diffD}d`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const FIELD_TYPES = {
  text: { label: "Texto", icon: TextFieldsIcon, chipClass: "chipText" },
  whatsapp: { label: "WhatsApp", icon: WhatsAppIcon, chipClass: "chipWhatsapp" },
  cpf: { label: "CPF", icon: FingerprintIcon, chipClass: "chipCpf" },
  email: { label: "Email", icon: EmailIcon, chipClass: "chipEmail" },
  currency: { label: "Moeda", icon: AttachMoneyIcon, chipClass: "chipCurrency" },
  checklist: { label: "Subtarefas", icon: PlaylistAddCheckIcon, chipClass: "chipText" },
};

const PRESETS = {
  leads: {
    label: "Leads",
    description: "Nome, CPF, WhatsApp, Email, Endereço, Valor",
    icon: Users,
    fields: [
      { type: "text", label: "Nome" },
      { type: "cpf", label: "CPF" },
      { type: "whatsapp", label: "WhatsApp" },
      { type: "email", label: "Email" },
      { type: "text", label: "Endereço" },
      { type: "currency", label: "Valor" },
    ],
  },
};

let fieldIdCounter = 0;
const generateFieldId = () => {
  fieldIdCounter += 1;
  return `f${Date.now()}_${fieldIdCounter}`;
};

const LeadDetailModal = ({ open, lead, statuses, onClose, onUpdateStatus, onDelete, onLeadUpdated }) => {
  const classes = useStyles();
  // Estilos compartilhados (CreateLeadModal usa o mesmo módulo). Mantém
  // o look-and-feel de prioridade/atribuído/SLA idêntico nos dois modais.
  const leadSharedClasses = useLeadSharedStyles();
  const { user } = useContext(AuthContext);
  // Reformular com IA: hoje, aberto a todos; com a regra nova, ai.assist.
  const { can } = usePermission();
  const canAiAssist = can("ai.assist", true);
  const [uploading, setUploading] = useState(false);
  const dialogRef = useRef(null);
  const descriptionEditorRef = useRef(null);
  const descriptionDisplayRef = useRef(null);

  // Inline edit: title
  const [titleDraft, setTitleDraft] = useState('');
  const [titleDirty, setTitleDirty] = useState(false);

  // Inline edit: description
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [descriptionExpanded, setDescriptionExpanded] = useState(true);
  const [aiReformulateOpen, setAiReformulateOpen] = useState(false);

  // Users list for assignment dropdown
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (!open) return;
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsers(data || []);
      } catch (err) { /* silent */ }
    };
    fetchUsers();
  }, [open]);

  // Attachment context menu
  const [attachMenuAnchor, setAttachMenuAnchor] = useState(null);
  const [attachMenuIndex, setAttachMenuIndex] = useState(null);

  const handleOpenAttachMenu = (e, index) => {
    e.stopPropagation();
    setAttachMenuAnchor(e.currentTarget);
    setAttachMenuIndex(index);
  };

  const handleCloseAttachMenu = () => {
    setAttachMenuAnchor(null);
    setAttachMenuIndex(null);
  };

  // Dynamic fields editing
  const [editFields, setEditFields] = useState([]);
  const [addFieldMenuAnchor, setAddFieldMenuAnchor] = useState(null);
  const [fieldMenuAnchor, setFieldMenuAnchor] = useState(null);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [editingLabelFieldId, setEditingLabelFieldId] = useState(null);
  const [fieldsDirty, setFieldsDirty] = useState(false);

  // Checklist field inline editing
  const [checklistAddingFor, setChecklistAddingFor] = useState(null);
  const [checklistNewText, setChecklistNewText] = useState('');
  const [checklistEditingItem, setChecklistEditingItem] = useState(null);
  const [checklistEditText, setChecklistEditText] = useState('');

  // Comments
  const [commentText, setCommentText] = useState('');

  // Pending file uploads
  const [pendingFiles, setPendingFiles] = useState([]); // { file: File, preview?: string }
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [pendingCoverIndex, setPendingCoverIndex] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Initialize fields from lead data (only once per open, not on every lead update)
  // For legacy cards (no customFields.fields), generate fields from flat columns
  const prevLeadIdRef = useRef(null);
  useEffect(() => {
    if (open && lead && prevLeadIdRef.current !== lead.id) {
      let existingFields = lead.customFields?.fields;
      if (!existingFields || existingFields.length === 0) {
        // Build fields from flat columns for legacy cards
        const flatFields = [];
        if (lead.name) flatFields.push({ id: generateFieldId(), type: 'text', label: 'Nome', value: lead.name, showOnCard: true });
        if (lead.phone) flatFields.push({ id: generateFieldId(), type: 'whatsapp', label: 'Telefone', value: lead.phone, showOnCard: true });
        if (lead.email) flatFields.push({ id: generateFieldId(), type: 'email', label: 'E-mail', value: lead.email, showOnCard: false });
        if (lead.cpf) flatFields.push({ id: generateFieldId(), type: 'cpf', label: 'CPF', value: lead.cpf, showOnCard: false });
        if (lead.address) flatFields.push({ id: generateFieldId(), type: 'text', label: 'Endereço', value: lead.address, showOnCard: false });
        if (lead.serviceOrder) flatFields.push({ id: generateFieldId(), type: 'text', label: 'Ordem de Serviço', value: lead.serviceOrder, showOnCard: false });
        existingFields = flatFields;
      }
      setEditFields(existingFields.map(f => ({ ...f })));
      setTitleDraft(lead.name || '');
      setTitleDirty(false);
      setFieldsDirty(false);
      // Hidrata o Quill com a descrição salva no card (HTML sanitizado).
      setDescriptionDraft(sanitizeHtml(lead.customFields?.description || ''));
      prevLeadIdRef.current = lead.id;
    }
  }, [open, lead]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset editing state when modal closes
  useEffect(() => {
    if (!open) {
      setTitleDraft('');
      setTitleDirty(false);
      setEditingDescription(false);
      setDescriptionExpanded(true);
      setAttachMenuAnchor(null);
      setAttachMenuIndex(null);
      setEditFields([]);
      // Clean up pending file previews
      pendingFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
      setPendingFiles([]);
      setPendingCoverIndex(null);
      setIsDraggingFile(false);
      setUploadingFiles(false);

      setAddFieldMenuAnchor(null);
      setFieldMenuAnchor(null);
      setActiveFieldId(null);
      setEditingLabelFieldId(null);
      setFieldsDirty(false);
      setChecklistAddingFor(null);
      setChecklistNewText('');
      setChecklistEditingItem(null);
      setChecklistEditText('');
      setCommentText('');
      prevLeadIdRef.current = null;
    }
  }, [open]);

  const getStatusById = (statusId) => {
    return statuses.find(s => s.id === statusId) || { name: i18n.t("externalLeads.defaultStatus"), color: '#94a3b8' };
  };

  // Upload files to server and return attachment objects
  const uploadFiles = useCallback(async (fileList) => {
    const formData = new FormData();
    for (const file of fileList) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} excede o limite de 50MB`);
        return null;
      }
      formData.append("files", file);
    }
    const { data } = await api.post("/external-leads/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data; // [{ name, url, type }]
  }, []);

  // Update lead's attachments and/or cover on backend
  const updateLeadData = useCallback(async (leadId, updates) => {
    try {
      const { data } = await api.put(`/external-leads/${leadId}`, updates);
      if (onLeadUpdated) onLeadUpdated(data);
    } catch (err) {
      toastError(err);
    }
  }, [onLeadUpdated]);

  // Ctrl+V paste handler
  const handlePaste = useCallback(async (e) => {
    if (!lead || !open) return;
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length === 0) return;
    e.preventDefault();

    setUploading(true);
    try {
      const uploaded = await uploadFiles(imageFiles);
      if (!uploaded) { setUploading(false); return; }

      // Merge with existing attachments
      const currentAttachments = lead.attachments || [];
      const newAttachments = [...currentAttachments, ...uploaded];

      await updateLeadData(lead.id, { attachments: newAttachments });
      toast.success(`${imageFiles.length} imagem(ns) colada(s)`);
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  }, [lead, open, uploadFiles, updateLeadData]);

  // Register/unregister paste listener
  useEffect(() => {
    if (!open) return;
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [open, handlePaste]);

  // Set an image as cover
  const handleSetCover = useCallback(async (imageUrl) => {
    if (!lead) return;
    const currentCustomFields = lead.customFields || {};
    const newCustomFields = { ...currentCustomFields, coverUrl: imageUrl };
    await updateLeadData(lead.id, { customFields: newCustomFields });
    toast.success("Capa atualizada");
  }, [lead, updateLeadData]);

  // Remove cover
  const handleRemoveCover = useCallback(async () => {
    if (!lead) return;
    const currentCustomFields = { ...(lead.customFields || {}) };
    delete currentCustomFields.coverUrl;
    await updateLeadData(lead.id, { customFields: currentCustomFields });
    toast.success("Capa removida");
  }, [lead, updateLeadData]);

  // Delete an attachment
  const handleDeleteAttachment = useCallback(async (index) => {
    if (!lead) return;
    const currentAttachments = lead.attachments || [];
    const removedUrl = currentAttachments[index]?.url;
    const newAttachments = currentAttachments.filter((_, i) => i !== index);

    // If the deleted attachment was the cover, remove cover too
    const updates = { attachments: newAttachments };
    if (removedUrl && lead.customFields?.coverUrl === removedUrl) {
      const newCustomFields = { ...(lead.customFields || {}) };
      delete newCustomFields.coverUrl;
      updates.customFields = newCustomFields;
    }

    await updateLeadData(lead.id, updates);
    toast.success("Anexo removido");
  }, [lead, updateLeadData]);

  // --- Pending file upload management ---
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  const addPendingFiles = useCallback((newFiles) => {
    const validFiles = [];
    for (const file of newFiles) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} excede o limite de 50MB`);
        continue;
      }
      const isImage = file.type.startsWith("image/");
      validFiles.push({
        file,
        preview: isImage ? URL.createObjectURL(file) : null,
      });
    }
    setPendingFiles(prev => [...prev, ...validFiles]);
  }, []);

  const removePendingFile = useCallback((index) => {
    setPendingFiles(prev => {
      const removed = prev[index];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
    setPendingCoverIndex(prev => {
      if (prev === null) return null;
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
  }, []);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) addPendingFiles(droppedFiles);
  }, [addPendingFiles]);

  const handleFileSelect = useCallback((e) => {
    const selected = Array.from(e.target.files);
    if (selected.length > 0) addPendingFiles(selected);
    e.target.value = "";
  }, [addPendingFiles]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getPendingFileIcon = (file) => {
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (file.type.startsWith("image/")) return <ImageIcon className={classes.pendingFileIconSmall} style={{ color: "#8b5cf6" }} />;
    if (ext === "pdf") return <FileText className={classes.pendingFileIconSmall} style={{ color: "#ef4444" }} />;
    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt"].includes(ext))
      return <FileText className={classes.pendingFileIconSmall} style={{ color: "#3b82f6" }} />;
    return <File className={classes.pendingFileIconSmall} style={{ color: "#94a3b8" }} />;
  };

  const handleUploadPendingFiles = useCallback(async () => {
    if (!lead || pendingFiles.length === 0) return;
    setUploadingFiles(true);
    try {
      const uploaded = await uploadFiles(pendingFiles.map(f => f.file));
      if (!uploaded) { setUploadingFiles(false); return; }

      const currentAttachments = lead.attachments || [];
      const newAttachments = [...currentAttachments, ...uploaded];
      const updates = { attachments: newAttachments };

      // If user chose a pending file as cover
      if (pendingCoverIndex !== null && uploaded[pendingCoverIndex]) {
        const coverUrl = uploaded[pendingCoverIndex].url;
        const newCustomFields = { ...(lead.customFields || {}), coverUrl };
        updates.customFields = newCustomFields;
      }

      await updateLeadData(lead.id, updates);
      // Clean up previews
      pendingFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
      setPendingFiles([]);
      setPendingCoverIndex(null);
      toast.success(`${uploaded.length} arquivo(s) enviado(s)`);
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingFiles(false);
    }
  }, [lead, pendingFiles, pendingCoverIndex, uploadFiles, updateLeadData]);

  // Populate description display with sanitized HTML
  useEffect(() => {
    if (descriptionDisplayRef.current && lead?.customFields?.description && !editingDescription) {
      const cleaned = sanitizeHtml(lead.customFields.description);
      const parser = new DOMParser();
      const doc = parser.parseFromString(cleaned, 'text/html');
      descriptionDisplayRef.current.textContent = '';
      Array.from(doc.body.childNodes).forEach(node => {
        descriptionDisplayRef.current.appendChild(node.cloneNode(true));
      });
    }
  }, [lead?.customFields?.description, editingDescription]);

  // Safely set sanitized HTML content into an element via DOM API
  const setSanitizedContent = useCallback((element, rawHtml) => {
    if (!element) return;
    const cleaned = sanitizeHtml(rawHtml);
    // Use DOM parser to safely set content
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleaned, 'text/html');
    element.textContent = ''; // clear
    Array.from(doc.body.childNodes).forEach(node => {
      element.appendChild(node.cloneNode(true));
    });
  }, []);

  // Save description inline (supports HTML)
  const handleStartEditDescription = useCallback(() => {
    if (!lead) return;
    const rawDesc = lead.customFields?.description || '';
    setDescriptionDraft(rawDesc);
    setEditingDescription(true);
    setDescriptionExpanded(true);
    // Populate editor after render
    setTimeout(() => {
      if (descriptionEditorRef.current) {
        setSanitizedContent(descriptionEditorRef.current, rawDesc);
        descriptionEditorRef.current.focus();
        // Move cursor to end
        const sel = window.getSelection();
        if (descriptionEditorRef.current.childNodes.length > 0) {
          sel.selectAllChildren(descriptionEditorRef.current);
          sel.collapseToEnd();
        }
      }
    }, 50);
  }, [lead, setSanitizedContent]);

  const handleSaveDescription = useCallback(async () => {
    if (!lead) return;
    const rawHtml = descriptionEditorRef.current
      ? descriptionEditorRef.current.innerHTML
      : descriptionDraft;
    const sanitized = sanitizeHtml(rawHtml).trim();
    // Check if empty (only whitespace or empty tags)
    const textOnly = descriptionEditorRef.current
      ? descriptionEditorRef.current.innerText.trim()
      : sanitized.replace(/<[^>]*>/g, '').trim();
    const currentDescription = lead.customFields?.description || '';
    if (sanitized === currentDescription) {
      setEditingDescription(false);
      return;
    }
    const newCustomFields = { ...(lead.customFields || {}), description: textOnly ? sanitized : undefined };
    if (!textOnly) delete newCustomFields.description;
    await updateLeadData(lead.id, { customFields: newCustomFields });
    setEditingDescription(false);
    toast.success("Descrição atualizada");
  }, [lead, descriptionDraft, updateLeadData]);

  const handleCancelDescription = useCallback(() => {
    setEditingDescription(false);
  }, []);

  // --- Dynamic field management ---
  const addField = (type, label) => {
    const field = { id: generateFieldId(), type, label, value: '', showOnCard: false };
    if (type === 'checklist') {
      field.items = [];
      field.showOnCard = true;
    }
    setEditFields(prev => [...prev, field]);
    setFieldsDirty(true);
  };

  const addPreset = (presetKey) => {
    const preset = PRESETS[presetKey];
    if (!preset) return;
    const newFields = preset.fields.map(f => ({
      id: generateFieldId(), type: f.type, label: f.label, value: '', showOnCard: false,
    }));
    setEditFields(prev => [...prev, ...newFields]);
    setAddFieldMenuAnchor(null);
    setFieldsDirty(true);
  };

  const addSingleField = (type) => {
    const typeInfo = FIELD_TYPES[type];
    addField(type, typeInfo?.label || 'Campo');
    setAddFieldMenuAnchor(null);
  };

  const updateFieldLocal = (fieldId, key, value) => {
    setEditFields(prev => prev.map(f => f.id === fieldId ? { ...f, [key]: value } : f));
    setFieldsDirty(true);
  };

  const removeFieldLocal = (fieldId) => {
    setEditFields(prev => prev.filter(f => f.id !== fieldId));
    setFieldMenuAnchor(null);
    setActiveFieldId(null);
    setFieldsDirty(true);
  };

  const toggleShowOnCard = (fieldId) => {
    setEditFields(prev => prev.map(f => f.id === fieldId ? { ...f, showOnCard: !f.showOnCard } : f));
    setFieldsDirty(true);
  };

  // --- Comment handlers ---
  const handleAddComment = useCallback(async () => {
    const text = commentText.trim();
    if (!text || !lead || !user) return;
    const comment = {
      id: `cm_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      text,
      userId: user.id,
      userName: user.name,
      createdAt: new Date().toISOString(),
    };
    const existingComments = lead.customFields?.comments || [];
    const newCustomFields = { ...(lead.customFields || {}), comments: [...existingComments, comment] };
    await updateLeadData(lead.id, { customFields: newCustomFields });
    setCommentText('');
  }, [commentText, lead, user, updateLeadData]);

  const handleDeleteComment = useCallback(async (commentId) => {
    if (!lead) return;
    const existingComments = lead.customFields?.comments || [];
    const newComments = existingComments.filter(c => c.id !== commentId);
    const newCustomFields = { ...(lead.customFields || {}), comments: newComments };
    await updateLeadData(lead.id, { customFields: newCustomFields });
  }, [lead, updateLeadData]);

  // Save all (title + fields + description) to backend
  const descriptionDirty = (() => {
    const current = (lead?.customFields?.description || '').trim();
    const draft = sanitizeHtml(descriptionDraft || '').trim();
    return current !== draft;
  })();

  const hasPendingChanges = titleDirty || fieldsDirty || descriptionDirty;

  const handleSaveAll = useCallback(async () => {
    if (!lead) return;

    // Validate title
    const trimmedTitle = titleDraft.trim();
    if (!trimmedTitle) {
      toast.error("Título não pode ficar vazio");
      return;
    }

    const currentCustomFields = { ...(lead.customFields || {}) };
    currentCustomFields.fields = editFields.map(f => {
      const base = { id: f.id, type: f.type, label: f.label, value: f.value, showOnCard: f.showOnCard };
      if (f.type === 'checklist') base.items = f.items || [];
      return base;
    });

    // Sync description into customFields
    const sanitizedDesc = sanitizeHtml(descriptionDraft || '');
    if (quillIsEmpty(sanitizedDesc)) {
      delete currentCustomFields.description;
    } else {
      currentCustomFields.description = sanitizedDesc;
    }

    // Extract flat fields for backward compat (clear if field was removed)
    const whatsappField = editFields.find(f => f.type === 'whatsapp' && f.value);
    const emailField = editFields.find(f => f.type === 'email' && f.value);
    const cpfField = editFields.find(f => f.type === 'cpf' && f.value);
    const addressField = editFields.find(f => f.label.toLowerCase() === 'endereço' && f.value);
    const serviceOrderField = editFields.find(f => f.label.toLowerCase() === 'ordem de serviço' && f.value);

    const updates = {
      customFields: currentCustomFields,
      name: trimmedTitle,
      phone: whatsappField?.value || '',
      email: emailField?.value || '',
      cpf: cpfField?.value || '',
      address: addressField?.value || '',
      serviceOrder: serviceOrderField?.value || '',
    };

    await updateLeadData(lead.id, updates);
    setTitleDirty(false);
    setFieldsDirty(false);
    toast.success('Card atualizado');
  }, [lead, titleDraft, editFields, descriptionDraft, updateLeadData]);

  // --- Checklist field helpers (inline in fields array) ---
  const addChecklistItem = (fieldId, text) => {
    setEditFields(prev => prev.map(f => {
      if (f.id !== fieldId) return f;
      const item = { id: `c${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, text, done: false };
      return { ...f, items: [...(f.items || []), item] };
    }));
    setFieldsDirty(true);
  };

  const toggleChecklistItem = (fieldId, itemId) => {
    setEditFields(prev => prev.map(f => {
      if (f.id !== fieldId) return f;
      return { ...f, items: (f.items || []).map(c => c.id === itemId ? { ...c, done: !c.done } : c) };
    }));
    setFieldsDirty(true);
  };

  const deleteChecklistItem = (fieldId, itemId) => {
    setEditFields(prev => prev.map(f => {
      if (f.id !== fieldId) return f;
      return { ...f, items: (f.items || []).filter(c => c.id !== itemId) };
    }));
    setFieldsDirty(true);
  };

  const renameChecklistItem = (fieldId, itemId, text) => {
    if (!text.trim()) return;
    setEditFields(prev => prev.map(f => {
      if (f.id !== fieldId) return f;
      return { ...f, items: (f.items || []).map(c => c.id === itemId ? { ...c, text: text.trim() } : c) };
    }));
    setFieldsDirty(true);
  };

  const renderChecklistField = (field) => {
    const items = field.items || [];
    const total = items.length;
    const done = items.filter(c => c.done).length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;

    return (
      <div style={{ width: '100%' }}>
        {total > 0 && (
          <div className={classes.checklistProgress}>
            <div className={classes.checklistBar}>
              <div
                className={classes.checklistBarFill}
                style={{
                  width: `${pct}%`,
                  backgroundColor: pct === 100 ? '#22c55e' : '#3b82f6',
                }}
              />
            </div>
            <Typography className={classes.checklistPercent}>
              {done}/{total}
            </Typography>
          </div>
        )}

        {items.map((item) => (
          <div key={item.id} className={classes.checklistItem}>
            <Checkbox
              checked={item.done}
              onChange={() => toggleChecklistItem(field.id, item.id)}
              className={classes.checklistCheckbox}
              size="small"
            />
            <div className={classes.checklistTextWrapper}>
              {checklistEditingItem?.fieldId === field.id && checklistEditingItem?.itemId === item.id ? (
                <TextField
                  className={classes.checklistInput}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={checklistEditText}
                  onChange={(e) => setChecklistEditText(e.target.value)}
                  onBlur={() => {
                    renameChecklistItem(field.id, item.id, checklistEditText);
                    setChecklistEditingItem(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      renameChecklistItem(field.id, item.id, checklistEditText);
                      setChecklistEditingItem(null);
                    }
                    if (e.key === 'Escape') setChecklistEditingItem(null);
                  }}
                  autoFocus
                />
              ) : (
                <Typography
                  className={item.done ? classes.checklistTextDone : classes.checklistText}
                  onClick={() => {
                    setChecklistEditingItem({ fieldId: field.id, itemId: item.id });
                    setChecklistEditText(item.text);
                  }}
                >
                  {item.text}
                </Typography>
              )}
            </div>
            <IconButton
              className={classes.checklistDeleteBtn}
              size="small"
              onClick={() => deleteChecklistItem(field.id, item.id)}
            >
              <X size={14} />
            </IconButton>
          </div>
        ))}

        {checklistAddingFor === field.id ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <TextField
              className={classes.checklistInput}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Nome da subtarefa..."
              value={checklistNewText}
              onChange={(e) => setChecklistNewText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (checklistNewText.trim()) {
                    addChecklistItem(field.id, checklistNewText.trim());
                    setChecklistNewText('');
                  }
                }
                if (e.key === 'Escape') { setChecklistAddingFor(null); setChecklistNewText(''); }
              }}
              autoFocus
            />
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                if (checklistNewText.trim()) {
                  addChecklistItem(field.id, checklistNewText.trim());
                  setChecklistNewText('');
                }
              }}
              disabled={!checklistNewText.trim()}
              style={{ textTransform: 'none', borderRadius: 8, minWidth: 50 }}
            >
              Criar
            </Button>
            <Button
              size="small"
              onClick={() => { setChecklistAddingFor(null); setChecklistNewText(''); }}
              style={{ textTransform: 'none', borderRadius: 8 }}
            >
              Cancelar
            </Button>
          </div>
        ) : (
          <button
            className={classes.checklistAddBtn}
            type="button"
            onClick={() => { setChecklistAddingFor(field.id); setChecklistNewText(''); }}
          >
            <Plus size={16} />
            Adicionar item
          </button>
        )}
      </div>
    );
  };

  // Render field input with masks
  const renderFieldInput = (field) => {
    const commonProps = {
      variant: 'outlined',
      size: 'small',
      fullWidth: true,
      className: classes.fieldTextField,
      label: field.label,
      value: field.value,
    };

    switch (field.type) {
      case 'whatsapp':
        return (
          <InputMask
            mask="(99) 99999-9999"
            value={field.value}
            onChange={(e) => updateFieldLocal(field.id, 'value', e.target.value)}
          >
            {(inputProps) => (
              <TextField {...inputProps} {...commonProps} onChange={undefined} />
            )}
          </InputMask>
        );
      case 'cpf':
        return (
          <InputMask
            mask="999.999.999-99"
            value={field.value}
            onChange={(e) => updateFieldLocal(field.id, 'value', e.target.value)}
          >
            {(inputProps) => (
              <TextField {...inputProps} {...commonProps} onChange={undefined} />
            )}
          </InputMask>
        );
      case 'currency':
        return (
          <NumberFormat
            customInput={TextField}
            variant="outlined"
            size="small"
            fullWidth
            className={classes.fieldTextField}
            label={field.label}
            value={field.value === '' ? '' : Number(field.value)}
            thousandSeparator="."
            decimalSeparator=","
            prefix="R$ "
            decimalScale={2}
            allowNegative={false}
            isNumericString={false}
            onValueChange={(values) =>
              updateFieldLocal(field.id, 'value', values.floatValue !== undefined ? String(values.floatValue) : '')
            }
          />
        );
      case 'email':
        return (
          <TextField
            {...commonProps}
            type="email"
            onChange={(e) => updateFieldLocal(field.id, 'value', e.target.value)}
          />
        );
      case 'checklist':
        return renderChecklistField(field);
      default:
        return (
          <TextField
            {...commonProps}
            onChange={(e) => updateFieldLocal(field.id, 'value', e.target.value)}
          />
        );
    }
  };

  const getChipClass = (type) => {
    const info = FIELD_TYPES[type];
    return info ? classes[info.chipClass] : classes.chipText;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className={classes.modalDialog}
    >
      {lead && (
        <>
          {/* Header MICRO — estilo Jira/Linear: breadcrumb + close.
              Status migrou pra dentro do bloco Detalhes (não fica mais
              perdido no topo como se fosse um botão). */}
          <header className={leadSharedClasses.modalHeader}>
            <span className={leadSharedClasses.modalHeaderCrumb}>
              <span className={leadSharedClasses.modalHeaderCrumbDot}
                style={{ background: getStatusById(lead.statusId).color }} />
              Editar card
            </span>
            <button type="button" className={leadSharedClasses.modalHeaderCloseBtn}
              onClick={onClose} aria-label="Fechar (Esc)">
              <X size={18} strokeWidth={1.75} />
            </button>
          </header>

          {/* Título HERO logo abaixo do header — protagonista visual */}
          <div className={leadSharedClasses.modalTitleHero}>
            <CardTitleHero
              value={titleDraft}
              onChange={(v) => {
                setTitleDraft(v);
                setTitleDirty(v !== lead.name);
              }}
              placeholder="Título do card"
              error={titleDirty && !titleDraft.trim()}
              errorText="Título não pode ficar vazio"
            />
          </div>
          <DialogContent className={classes.modalContent} ref={dialogRef}>
            {/* Cover image */}
            {lead.customFields?.coverUrl && (
              <>
                <img
                  src={lead.customFields.coverUrl}
                  alt="Capa"
                  className={classes.coverImage}
                  onError={(e) => { e.target.style.display = 'none'; }}
                  onClick={() => window.open(lead.customFields.coverUrl, '_blank')}
                />
                <div className={classes.coverActions}>
                  <Button
                    size="small"
                    variant="outlined"
                    className={classes.coverBtn}
                    onClick={handleRemoveCover}
                  >
                    Remover capa
                  </Button>
                </div>
              </>
            )}

            {/* Descrição — editor Quill premium (mesmo do CreateLeadModal).
                Autosave no blur (sem botão Salvar duplicado). */}
            <div className={classes.infoSection}>
              <div className={leadSharedClasses.sectionHeaderRow}>
                <span className={leadSharedClasses.sectionLabel}>Descrição</span>
                {canAiAssist && (
                  <button
                    type="button"
                    className={leadSharedClasses.aiPill || ''}
                    onClick={() => setAiReformulateOpen(true)}
                    disabled={!lead.customFields?.description}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '4px 10px', borderRadius: 999,
                      border: 'none',
                      background: 'linear-gradient(135deg, rgba(139,92,246,0.16) 0%, rgba(91,124,255,0.16) 100%)',
                      color: '#C4B5FD',
                      fontSize: 11, fontWeight: 600,
                      cursor: lead.customFields?.description ? 'pointer' : 'not-allowed',
                      fontFamily: 'inherit',
                      opacity: lead.customFields?.description ? 1 : 0.4,
                    }}
                    title="Reformular com IA"
                  >
                    <Sparkles size={11} strokeWidth={2.25} />
                    Reformular com IA
                  </button>
                )}
              </div>
              <CardDescriptionEditor
                value={descriptionDraft}
                onChange={(html) => setDescriptionDraft(html)}
                onBlur={async () => {
                  // Autosave: persiste no servidor quando o usuário sai do editor.
                  const html = descriptionDraft;
                  const sanitized = sanitizeHtml(html);
                  const textOnly = quillIsEmpty(sanitized) ? '' : sanitized;
                  if ((textOnly || '') === (lead.customFields?.description || '')) return;
                  const newCustomFields = { ...(lead.customFields || {}) };
                  if (textOnly) newCustomFields.description = textOnly;
                  else delete newCustomFields.description;
                  await updateLeadData(lead.id, { customFields: newCustomFields });
                }}
              />
            </div>

            {/* Campos personalizados — componente shared (mesmo visual do
                modal de criação, popover de "Adicionar" e menu de 3 pontinhos
                premium). State controlado: editFields + handlers locais. */}
            <div className={classes.infoSection}>
              <span className={leadSharedClasses.sectionLabel}
                style={{ display: 'block', marginBottom: 10 }}>
                Campos personalizados
              </span>
              <CardCustomFieldsSection
                fields={editFields}
                onAddField={(type, label) => addField(type, label)}
                onAddPreset={(presetKey) => addPreset(presetKey)}
                onUpdateField={(fieldId, key, value) => updateFieldLocal(fieldId, key, value)}
                onRemoveField={(fieldId) => removeFieldLocal(fieldId)}
                onToggleShowOnCard={(fieldId) => toggleShowOnCard(fieldId)}
                checklistState={{ addingFor: checklistAddingFor, newText: checklistNewText }}
                checklistActions={{
                  setAddingFor: setChecklistAddingFor,
                  setNewText: setChecklistNewText,
                  addItem: addChecklistItem,
                  toggleItem: toggleChecklistItem,
                  deleteItem: deleteChecklistItem,
                  renameItem: renameChecklistItem,
                  // Habilita o "Salvar" assim que o texto da subtarefa muda.
                  markDirty: () => setFieldsDirty(true),
                }}
              />
            </div>

            {/* Status — pills com cor da coluna. Substitui o antigo "Alterar
                Status" select e o chip perdido no topo. */}
            <div className={classes.infoSection}>
              <span className={leadSharedClasses.sectionLabel}
                style={{ display: 'block', marginBottom: 10 }}>
                Status
              </span>
              <div className={leadSharedClasses.statusPills}>
                {statuses.map((status) => {
                  const isActive = lead.statusId === status.id;
                  return (
                    <button
                      key={status.id}
                      type="button"
                      className={`${leadSharedClasses.statusPill} ${isActive ? leadSharedClasses.statusPillActive : ''}`}
                      onClick={() => { if (!isActive) onUpdateStatus(status.id); }}
                      style={isActive ? {
                        backgroundColor: status.color,
                      } : undefined}
                    >
                      <span className={leadSharedClasses.statusPillDot}
                        style={{ backgroundColor: isActive ? '#FFFFFF' : status.color }} />
                      {status.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Details: Priority, Assigned, SLA — visual unificado com CreateLeadModal */}
            <div className={classes.infoSection}>
              <span className={leadSharedClasses.sectionLabel}
                style={{ display: 'block', marginBottom: 10 }}>
                Detalhes
              </span>
              <div className={classes.detailsEditGrid}>
                <div className={leadSharedClasses.detailCell}>
                  <span className={leadSharedClasses.detailCellLabel}>Prioridade</span>
                  <PrioritySegmented
                    value={lead.priority || 'normal'}
                    onChange={async (newPriority) => {
                      await updateLeadData(lead.id, { priority: newPriority });
                      toast.success('Prioridade atualizada');
                    }}
                  />
                </div>
                <div className={leadSharedClasses.detailCell}>
                  <span className={leadSharedClasses.detailCellLabel}>Atribuído</span>
                  <AssigneePicker
                    users={users}
                    value={lead.assignedUserId || ''}
                    onChange={async (newUserId) => {
                      await updateLeadData(lead.id, { assignedUserId: newUserId || null });
                      toast.success('Atribuição atualizada');
                    }}
                  />
                </div>
                <div className={leadSharedClasses.detailCell}>
                  <span className={leadSharedClasses.detailCellLabel}>Prazo (SLA)</span>
                  <SlaPopoverButton
                    value={lead.customFields?.slaDate || ''}
                    // Coluna de conclusão: prazo neutro, sem "Vencido". A lista
                    // de colunas pode chegar depois (Calendário), daí o fallback
                    // para o status que veio junto do card.
                    completed={!!(
                      (statuses || []).find(s => s.id === lead.statusId)?.isCompleted
                      ?? lead.status?.isCompleted
                    )}
                    onChange={async (slaDate) => {
                      const newCustomFields = { ...(lead.customFields || {}) };
                      if (slaDate) newCustomFields.slaDate = slaDate;
                      else delete newCustomFields.slaDate;
                      await updateLeadData(lead.id, { customFields: newCustomFields });
                      toast.success(slaDate ? 'Prazo atualizado' : 'Prazo removido');
                    }}
                  />
                </div>
              </div>
              {lead.customFields?.createdByUserName && (
                <Typography variant="caption" style={{
                  color: 'rgba(255,255,255,0.4)',
                  marginTop: 8,
                  display: 'block',
                  fontSize: 11,
                }}>
                  Criado por: {lead.customFields.createdByUserName}
                </Typography>
              )}
            </div>

            {/* Triagem por IA do card (só leitura; some sem triagem ou sem o módulo) */}
            <LeadTriageSection leadId={lead.id} className={classes.infoSection} titleClassName={leadSharedClasses.sectionLabel} />

            {/* Upload zone — add new files */}
            <div className={classes.infoSection}>
              <Typography className={classes.sectionTitle}>
                Anexos
              </Typography>

              {pendingFiles.length > 0 && (
                <div className={classes.pendingFileList}>
                  {pendingFiles.map((f, index) => {
                    const isImage = f.file.type.startsWith("image/");
                    const isCover = pendingCoverIndex === index;
                    return (
                      <div key={index} className={isCover ? classes.pendingFileItemCover : classes.pendingFileItem}>
                        {f.preview ? (
                          <img src={f.preview} alt="" className={classes.pendingFileThumb} />
                        ) : (
                          getPendingFileIcon(f.file)
                        )}
                        <Typography className={classes.pendingFileName} title={f.file.name}>
                          {f.file.name}
                        </Typography>
                        <Typography className={classes.pendingFileSize}>
                          {formatFileSize(f.file.size)}
                        </Typography>
                        {isImage && (
                          <Tooltip title={isCover ? "Remover capa" : "Usar como capa"}>
                            <IconButton
                              className={isCover ? classes.pendingFileCoverActive : classes.pendingFileCoverBtn}
                              size="small"
                              onClick={() => setPendingCoverIndex(isCover ? null : index)}
                            >
                              <ImageIcon size={14} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          className={classes.pendingFileRemove}
                          size="small"
                          onClick={() => removePendingFile(index)}
                        >
                          <X size={14} />
                        </IconButton>
                      </div>
                    );
                  })}
                </div>
              )}

              <div
                className={`${classes.uploadZone} ${isDraggingFile ? classes.uploadZoneDragging : ""}`}
                onClick={() => document.getElementById("lead-detail-file-input").click()}
                onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
                onDragLeave={() => setIsDraggingFile(false)}
                onDrop={handleFileDrop}
              >
                <UploadCloud className={classes.uploadIcon} />
                <Typography className={classes.uploadText}>
                  Clique ou arraste arquivos aqui
                </Typography>
                <Typography className={classes.uploadLimit}>
                  Imagens, PDFs, documentos — máx. 50MB por arquivo
                </Typography>
                <input
                  id="lead-detail-file-input"
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />
              </div>

              {pendingFiles.length > 0 && (
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleUploadPendingFiles}
                    disabled={uploadingFiles}
                    style={{ textTransform: 'none', borderRadius: 8 }}
                  >
                    {uploadingFiles ? 'Enviando...' : `Enviar ${pendingFiles.length} arquivo(s)`}
                    {uploadingFiles && <CircularProgress size={18} style={{ marginLeft: 8, color: '#fff' }} />}
                  </Button>
                </Box>
              )}
            </div>

            {/* Existing attachments */}
            {lead.attachments && lead.attachments.length > 0 && (
              <div className={classes.infoSection}>
                <Typography className={classes.sectionTitle}>
                  Arquivos enviados ({lead.attachments.length})
                </Typography>
                <div className={classes.attachmentGrid}>
                  {lead.attachments.map((attachment, index) => {
                    const url = attachment.url || '';
                    const name = attachment.name || attachment.type || `Anexo ${index + 1}`;
                    const ext = url.split('.').pop()?.toLowerCase() || '';
                    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
                    const isPdf = ext === 'pdf';
                    const isDoc = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(ext);
                    const isCover = lead.customFields?.coverUrl === url;

                    return (
                      <div key={index} className={classes.attachmentCardWrapper}>
                        <div
                          className={classes.attachmentCard}
                          onClick={() => window.open(url, '_blank')}
                          title={name}
                          style={isCover ? { border: '2px solid #8b5cf6' } : undefined}
                        >
                          {isImage ? (
                            <img
                              src={url}
                              alt={name}
                              className={classes.attachmentImage}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={classes.attachmentFilePlaceholder}
                            style={{ display: isImage ? 'none' : 'flex' }}
                          >
                            {isPdf ? (
                              <FileText className={classes.pdfIcon} />
                            ) : isDoc ? (
                              <FileText className={classes.docIcon} />
                            ) : (
                              <File className={classes.fileIcon} />
                            )}
                            <Typography variant="caption" style={{
                              color: isPdf ? '#ef4444' : isDoc ? '#3b82f6' : '#94a3b8',
                              fontWeight: 600,
                              textTransform: 'uppercase'
                            }}>
                              {ext || 'FILE'}
                            </Typography>
                          </div>
                          <div className={classes.attachmentLabel}>
                            {name}
                          </div>
                        </div>
                        <IconButton
                          className={classes.attachmentMenuBtn}
                          size="small"
                          onClick={(e) => handleOpenAttachMenu(e, index)}
                        >
                          <MoreVertical size={16} />
                        </IconButton>
                      </div>
                    );
                  })}
                </div>

                {/* Context menu for attachments */}
                <Menu
                  anchorEl={attachMenuAnchor}
                  open={Boolean(attachMenuAnchor)}
                  onClose={handleCloseAttachMenu}
                  getContentAnchorEl={null}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    style: {
                      borderRadius: 10,
                      minWidth: 180,
                    },
                  }}
                >
                  {attachMenuIndex !== null && (() => {
                    const att = lead.attachments?.[attachMenuIndex];
                    if (!att) return null;
                    const attUrl = att.url || '';
                    const attExt = attUrl.split('.').pop()?.toLowerCase() || '';
                    const attIsImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(attExt);
                    const attIsCover = lead.customFields?.coverUrl === attUrl;

                    return (
                      <>
                        {attIsImage && (
                          <MenuItem
                            className={classes.attachmentMenuItem}
                            onClick={() => {
                              if (attIsCover) {
                                handleRemoveCover();
                              } else {
                                handleSetCover(attUrl);
                              }
                              handleCloseAttachMenu();
                            }}
                          >
                            <ListItemIcon style={{ minWidth: 32 }}>
                              <Checkbox
                                checked={attIsCover}
                                size="small"
                                color="primary"
                                style={{ padding: 0 }}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary="Capa do card"
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </MenuItem>
                        )}
                        <MenuItem
                          className={classes.attachmentMenuItem}
                          onClick={() => {
                            handleDeleteAttachment(attachMenuIndex);
                            handleCloseAttachMenu();
                          }}
                        >
                          <ListItemIcon style={{ minWidth: 32 }}>
                            <Trash2 size={20} style={{ color: '#ef4444' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="Excluir anexo"
                            primaryTypographyProps={{ variant: 'body2', style: { color: '#ef4444' } }}
                          />
                        </MenuItem>
                      </>
                    );
                  })()}
                </Menu>
              </div>
            )}

            <Typography className={classes.pasteHint}>
              Ctrl+V para colar imagens da área de transferência
            </Typography>

            {/* Comments */}
            <Divider style={{ margin: '16px 0' }} />
            <div className={classes.infoSection}>
              <Typography className={classes.sectionTitle}>
                Comentários
                {(lead.customFields?.comments?.length || 0) > 0 && (
                  <span style={{ fontWeight: 400, opacity: 0.6, marginLeft: 6 }}>
                    ({lead.customFields.comments.length})
                  </span>
                )}
              </Typography>

              {lead.customFields?.comments?.length > 0 && (
                <div className={classes.commentList}>
                  {lead.customFields.comments.map((comment) => (
                    <div key={comment.id} className={classes.commentItemHover}>
                      <Avatar
                        className={classes.commentAvatar}
                        style={{ backgroundColor: stringToColor(comment.userName) }}
                      >
                        {getInitials(comment.userName)}
                      </Avatar>
                      <div className={classes.commentBody}>
                        <div className={classes.commentHeader}>
                          <Typography className={classes.commentAuthor}>
                            {comment.userName}
                          </Typography>
                          <Tooltip title={formatDate(comment.createdAt)} placement="top">
                            <Typography className={classes.commentTime}>
                              {formatCommentTime(comment.createdAt)}
                            </Typography>
                          </Tooltip>
                        </div>
                        <Typography className={classes.commentText}>
                          {comment.text}
                        </Typography>
                      </div>
                      {user && comment.userId === user.id && (
                        <IconButton
                          className={classes.commentDeleteBtn}
                          size="small"
                          onClick={() => handleDeleteComment(comment.id)}
                        >
                          <X size={14} />
                        </IconButton>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className={classes.commentInputRow}>
                <Avatar
                  className={classes.commentAvatar}
                  style={{ backgroundColor: stringToColor(user?.name) }}
                >
                  {getInitials(user?.name)}
                </Avatar>
                <TextField
                  className={classes.commentInput}
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={4}
                  placeholder="Escreva um comentário..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                <Button
                  className={classes.commentSendBtn}
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleAddComment}
                  disabled={!commentText.trim()}
                >
                  Enviar
                </Button>
              </div>
            </div>

            {/* Legacy flat customFields (WordPress webhook) */}
            {lead.customFields && !lead.customFields.fields && (() => {
              const metaKeys = ['description', 'createdByUserId', 'createdByUserName', 'coverUrl', 'checklist', 'fields', 'comments'];
              const entries = Object.entries(lead.customFields).filter(([key]) => !metaKeys.includes(key));
              return entries.length > 0 ? (
                <div className={classes.infoSection}>
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("externalLeads.modal.customFields")}
                  </Typography>
                  <div className={classes.infoGrid}>
                    {entries.map(([key, value]) => (
                      <div key={key} className={classes.infoItem}>
                        <Typography className={classes.infoLabel}>{key}</Typography>
                        <Typography className={classes.infoValue}>{String(value)}</Typography>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}


            {/* Metadados discretos no rodapé do body */}
            <Box display="flex" justifyContent="space-between" mt={2} style={{
              fontSize: 11,
              color: 'rgba(255,255,255,0.35)',
              flexWrap: 'wrap',
              gap: 8,
            }}>
              <span>
                {i18n.t("externalLeads.modal.receivedAt")} {formatDate(lead.createdAt)}
              </span>
              <span>
                {i18n.t("externalLeads.modal.viewedAt")} {formatDate(lead.viewedAt)}
              </span>
            </Box>
          </DialogContent>
          {/* Footer premium — Excluir | resumo SLA | Fechar/Salvar */}
          <footer className={leadSharedClasses.modalFooter}>
            <div className={leadSharedClasses.modalFooterLeft}>
              <Tooltip title="Excluir card" arrow>
                <button
                  type="button"
                  className={leadSharedClasses.modalDeleteBtn}
                  onClick={() => onDelete && onDelete(lead)}
                  aria-label="Excluir card"
                >
                  <Trash2 size={16} strokeWidth={1.75} />
                </button>
              </Tooltip>
            </div>
            <div className={leadSharedClasses.modalFooterCenter}>
              {lead.customFields?.slaDate ? (
                <>
                  <Calendar size={12} strokeWidth={2} />
                  SLA: {new Date(lead.customFields.slaDate).toLocaleString("pt-BR", {
                    day: "2-digit", month: "2-digit", year: "2-digit",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </>
              ) : (
                <>
                  <Calendar size={12} strokeWidth={2} />
                  Sem SLA definido
                </>
              )}
            </div>
            <div className={leadSharedClasses.modalFooterActions}>
              <button type="button" className={leadSharedClasses.modalCancelBtn} onClick={onClose}>
                {i18n.t("externalLeads.modal.close")}
              </button>
              <button
                type="button"
                className={leadSharedClasses.modalSubmitBtn}
                onClick={handleSaveAll}
                disabled={!hasPendingChanges}
              >
                Salvar
              </button>
            </div>
          </footer>
        </>
      )}
      {uploading && (
        <div className={classes.uploadingOverlay}>
          <CircularProgress style={{ color: '#fff' }} />
          <Typography style={{ color: '#fff', fontWeight: 600 }}>
            Enviando imagem...
          </Typography>
        </div>
      )}
      <AiReformulateDescriptionModal
        open={aiReformulateOpen}
        onClose={() => setAiReformulateOpen(false)}
        title={lead?.name || ''}
        originalDescription={descriptionEditorRef.current?.innerHTML || ''}
        onApply={(newHtml) => {
          if (descriptionEditorRef.current) {
            setSanitizedContent(descriptionEditorRef.current, newHtml);
          }
          setAiReformulateOpen(false);
        }}
      />
    </Dialog>
  );
};

export default LeadDetailModal;
